/**
 * Exact rational helpers (W1-01 core-time-math).
 *
 *  - integer helpers on safe JS integers (`gcd`, `lcm`, floor division) with exactness guards;
 *  - an arbitrary-precision rational `BigRational` (`Rat`) on BigInt, used as the fallback of the beat arithmetic
 *    (core/time/beat.ts) whenever a cross product leaves the safe-integer range, by the beat parser, and by
 *    callers that need exact re-timing (decimal BPMs);
 *  - exact decomposition of an f64 into its dyadic rational and continued-fraction best approximations.
 *
 * Everything here is pure and allocation-light; nothing mutates its inputs.
 */

/** 2^53 - 1. */
export const MAX_SAFE = Number.MAX_SAFE_INTEGER;

/** True for integers that JS numbers represent exactly (|x| <= 2^53 - 1). */
export function isSafeInt(x: number): boolean {
  return Number.isSafeInteger(x);
}

/** Greatest common divisor of two safe integers (signs ignored). gcd(0, 0) = 0. */
export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const r = x % y;
    x = y;
    y = r;
  }
  return x;
}

/** Least common multiple of two safe integers (signs ignored). Returns NaN when the result is not a safe integer. */
export function lcm(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  const g = gcd(a, b);
  const r = Math.abs(a / g) * Math.abs(b);
  return Number.isSafeInteger(r) ? r : Number.NaN;
}

/** floor(a / b) for safe integers, b !== 0, exact (no float rounding of the quotient). */
export function floorDiv(a: number, b: number): number {
  let q = Math.floor(a / b);
  // Repair the rare off-by-one caused by the rounded float quotient.
  const r = a - q * b;
  if (b > 0) {
    if (r < 0) q -= 1;
    else if (r >= b) q += 1;
  } else if (r > 0) q -= 1;
  else if (r <= b) q += 1;
  return q;
}

/** Euclidean-style modulo for a positive divisor: result in [0, b). */
export function modPos(a: number, b: number): number {
  const r = a % b;
  return r < 0 ? r + b : r;
}

// ---------------------------------------------------------------------------------------------------------------
// BigInt integer helpers
// ---------------------------------------------------------------------------------------------------------------

export function absBig(a: bigint): bigint {
  return a < 0n ? -a : a;
}

export function gcdBig(a: bigint, b: bigint): bigint {
  let x = absBig(a);
  let y = absBig(b);
  while (y !== 0n) {
    const r = x % y;
    x = y;
    y = r;
  }
  return x;
}

/** floor(a / b) for BigInt (BigInt division truncates toward zero). b !== 0n. */
export function floorDivBig(a: bigint, b: bigint): bigint {
  const q = a / b;
  const r = a % b;
  return r !== 0n && r < 0n !== b < 0n ? q - 1n : q;
}

/** round(a / b) with ties toward +infinity, b > 0n. */
export function roundHalfUpDivBig(a: bigint, b: bigint): bigint {
  return floorDivBig(2n * a + b, 2n * b);
}

// ---------------------------------------------------------------------------------------------------------------
// BigRational
// ---------------------------------------------------------------------------------------------------------------

/** Exact rational num/den: den > 0, gcd(|num|, den) = 1 (0 is 0/1). */
export interface BigRational {
  readonly num: bigint;
  readonly den: bigint;
}

function ratMake(num: bigint, den: bigint): BigRational {
  if (den === 0n) throw new RangeError('rational: zero denominator');
  let n = num;
  let d = den;
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  if (n === 0n) return { num: 0n, den: 1n };
  const g = gcdBig(n, d);
  return g === 1n ? { num: n, den: d } : { num: n / g, den: d / g };
}

function toBigInt(x: number | bigint, what: string): bigint {
  if (typeof x === 'bigint') return x;
  if (!Number.isInteger(x)) throw new RangeError(`rational: ${what} must be an integer (got ${String(x)})`);
  return BigInt(x);
}

/**
 * Exact value of a finite f64 as a dyadic rational (num / 2^k). Throws RangeError for NaN / infinities.
 * Multiplying by 2 is exact for every finite double below 2^1023, and a non-integer double is below 2^52.
 */
export function dyadicOf(x: number): BigRational {
  if (!Number.isFinite(x)) throw new RangeError(`rational: not a finite number (${String(x)})`);
  if (Number.isInteger(x)) return { num: BigInt(x), den: 1n };
  let y = x;
  let k = 0;
  while (!Number.isInteger(y)) {
    y *= 2;
    k += 1;
  }
  return ratMake(BigInt(y), 1n << BigInt(k));
}

/** Parses an exact decimal literal: `[+-]digits[.digits][e[+-]digits]` (also `.5`, `5.`). Null when malformed. */
function ratFromDecimal(src: string): BigRational | null {
  const m = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(src.trim());
  if (m === null) return null;
  const sign = m[1] ?? '';
  const intPart = m[2] ?? '';
  const fracPart = m[3] ?? '';
  const expPart = m[4];
  if (intPart.length === 0 && fracPart.length === 0) return null;
  // Guard against absurd exponents before building 10^k.
  const exp = expPart === undefined ? 0 : Number(expPart);
  if (!Number.isSafeInteger(exp) || Math.abs(exp) > 4096) return null;
  let num = BigInt((intPart || '0') + fracPart);
  let den = 10n ** BigInt(fracPart.length);
  if (exp > 0) num *= 10n ** BigInt(exp);
  else if (exp < 0) den *= 10n ** BigInt(-exp);
  if (sign === '-') num = -num;
  return ratMake(num, den);
}

function ratCmp(a: BigRational, b: BigRational): -1 | 0 | 1 {
  const x = a.num * b.den;
  const y = b.num * a.den;
  return x < y ? -1 : x > y ? 1 : 0;
}

/** x * 2^e applied in steps so that intermediate results neither overflow nor underflow prematurely. */
function ldexp(x: number, e: number): number {
  let v = x;
  let k = e;
  while (k > 1023) {
    v *= 2 ** 1023;
    k -= 1023;
  }
  while (k < -1022) {
    v *= 2 ** -1022;
    k += 1022;
  }
  return v * 2 ** k;
}

const SAFE_BIG = 9007199254740991n;
/** Exponent of the smallest subnormal double (2^-1074 = Number.MIN_VALUE). */
const SUBNORMAL_SHIFT = 1074n;

function bitLength(x: bigint): number {
  return x.toString(2).length;
}

/**
 * f64 value of num/den, correctly rounded (round to nearest, ties to even) for every input:
 *  - both parts safe integers: one IEEE division, which is correctly rounded by itself;
 *  - normal results: the BigInt quotient is taken with 66-67 significant bits and a sticky bit (OR-ed in when
 *    the division leaves a remainder), so the single rounding of `Number(bigint)` to 53 bits sees exactly the
 *    information it needs, and the final power-of-two scaling is exact;
 *  - subnormal results (|value| < 2^-1022): the value is rounded once, in units of 2^-1074, by hand.
 * Results beyond the double range give +-Infinity.
 */
function ratToF64(r: BigRational): number {
  const { num, den } = r;
  if (absBig(num) <= SAFE_BIG && den <= SAFE_BIG) return Number(num) / Number(den);
  const neg = num < 0n;
  const a = neg ? -num : num; // > 0: zero is 0/1 and took the safe path
  const nb = bitLength(a);
  const db = bitLength(den);
  // 2^(nb - db - 1) < a / den < 2^(nb - db + 1)
  if (nb - db < -1021 && a << 1022n < den) {
    // Subnormal: q = round_half_even(a * 2^1074 / den), result q * 2^-1074 (q <= 2^52, exact).
    const scaled = a << SUBNORMAL_SHIFT;
    let q = scaled / den;
    const twiceRem = 2n * (scaled - q * den);
    if (twiceRem > den || (twiceRem === den && (q & 1n) === 1n)) q += 1n;
    const v = Number(q) * Number.MIN_VALUE;
    return neg ? -v : v;
  }
  const shift = 66 - (nb - db);
  let q: bigint;
  let exact: boolean;
  if (shift >= 0) {
    const scaled = a << BigInt(shift);
    q = scaled / den;
    exact = q * den === scaled;
  } else {
    const d = den << BigInt(-shift);
    q = a / d;
    exact = q * d === a;
  }
  if (!exact) q |= 1n; // sticky bit: far below the 53-bit rounding position, it only breaks false ties
  const v = ldexp(Number(q), -shift);
  return neg ? -v : v;
}

function ratFloor(r: BigRational): bigint {
  return floorDivBig(r.num, r.den);
}

/**
 * Best rational approximation p/q of num/den with 1 <= q <= maxDen (continued fractions with the final
 * semiconvergent). On an exact tie the smaller denominator wins, then the larger value.
 */
export function bestRational(value: BigRational, maxDen: bigint): BigRational {
  if (maxDen < 1n) throw new RangeError('rational: maxDen must be >= 1');
  const { num, den } = value;
  let p0 = 0n;
  let q0 = 1n;
  let p1 = 1n;
  let q1 = 0n;
  let a = num;
  let b = den;
  for (;;) {
    if (b === 0n) return { num: p1, den: q1 };
    const t = floorDivBig(a, b);
    const q2 = q0 + t * q1;
    if (q2 > maxDen) {
      // q1 >= 1 here: the first step always produces q = 1 <= maxDen.
      const m = (maxDen - q0) / q1;
      const ps = p0 + m * p1;
      const qs = q0 + m * q1;
      // |value - ps/qs| vs |value - p1/q1| scaled by den: |num*qs - ps*den| / qs vs |num*q1 - p1*den| / q1.
      const es = absBig(num * qs - ps * den) * q1;
      const ec = absBig(num * q1 - p1 * den) * qs;
      if (es < ec) return { num: ps, den: qs };
      if (ec < es) return { num: p1, den: q1 };
      if (qs !== q1) return qs < q1 ? { num: ps, den: qs } : { num: p1, den: q1 };
      return ps * q1 > p1 * qs ? { num: ps, den: qs } : { num: p1, den: q1 };
    }
    const p2 = p0 + t * p1;
    p0 = p1;
    q0 = q1;
    p1 = p2;
    q1 = q2;
    const r = a - t * b;
    a = b;
    b = r;
  }
}

/** The continued-fraction convergents of `value` with denominator <= maxDen, in increasing denominator order. */
export function convergents(value: BigRational, maxDen: bigint): BigRational[] {
  const out: BigRational[] = [];
  let p0 = 0n;
  let q0 = 1n;
  let p1 = 1n;
  let q1 = 0n;
  let a = value.num;
  let b = value.den;
  while (b !== 0n) {
    const t = floorDivBig(a, b);
    const p2 = p0 + t * p1;
    const q2 = q0 + t * q1;
    if (q2 > maxDen) break;
    out.push({ num: p2, den: q2 });
    p0 = p1;
    q0 = q1;
    p1 = p2;
    q1 = q2;
    const r = a - t * b;
    a = b;
    b = r;
  }
  return out;
}

/** Arbitrary-precision exact rational arithmetic (BigInt). Results are always reduced with a positive denominator. */
export const Rat = {
  ZERO: Object.freeze({ num: 0n, den: 1n }) as BigRational,
  ONE: Object.freeze({ num: 1n, den: 1n }) as BigRational,
  /** num/den, reduced. Numbers must be integers. Throws RangeError for a zero denominator. */
  of(num: number | bigint, den: number | bigint = 1n): BigRational {
    return ratMake(toBigInt(num, 'numerator'), toBigInt(den, 'denominator'));
  },
  fromDecimal: ratFromDecimal,
  /** Exact value of a finite f64. */
  fromF64: dyadicOf,
  add(a: BigRational, b: BigRational): BigRational {
    return a.den === b.den ? ratMake(a.num + b.num, a.den) : ratMake(a.num * b.den + b.num * a.den, a.den * b.den);
  },
  sub(a: BigRational, b: BigRational): BigRational {
    return a.den === b.den ? ratMake(a.num - b.num, a.den) : ratMake(a.num * b.den - b.num * a.den, a.den * b.den);
  },
  mul(a: BigRational, b: BigRational): BigRational {
    return ratMake(a.num * b.num, a.den * b.den);
  },
  /** Throws RangeError when dividing by zero. */
  div(a: BigRational, b: BigRational): BigRational {
    if (b.num === 0n) throw new RangeError('rational: division by zero');
    return ratMake(a.num * b.den, a.den * b.num);
  },
  neg(a: BigRational): BigRational {
    return a.num === 0n ? a : { num: -a.num, den: a.den };
  },
  abs(a: BigRational): BigRational {
    return a.num < 0n ? { num: -a.num, den: a.den } : a;
  },
  sign(a: BigRational): -1 | 0 | 1 {
    return a.num < 0n ? -1 : a.num > 0n ? 1 : 0;
  },
  cmp: ratCmp,
  eq(a: BigRational, b: BigRational): boolean {
    return a.num === b.num && a.den === b.den;
  },
  floor: ratFloor,
  isInteger(a: BigRational): boolean {
    return a.den === 1n;
  },
  toF64: ratToF64,
  /** Best approximation with denominator <= maxDen. */
  approx(a: BigRational, maxDen: number | bigint): BigRational {
    return bestRational(a, toBigInt(maxDen, 'maxDen'));
  },
  /** Nearest multiple of 1/div (ties toward +infinity). */
  roundTo(a: BigRational, div: number | bigint): BigRational {
    const d = toBigInt(div, 'div');
    if (d <= 0n) throw new RangeError('rational: div must be positive');
    return ratMake(roundHalfUpDivBig(a.num * d, a.den), d);
  },
  toString(a: BigRational): string {
    return a.den === 1n ? a.num.toString() : `${a.num.toString()}/${a.den.toString()}`;
  },
} as const;
